/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Enter the URL-selected workspace and sign out of the current session.
 * @state-owner TanStack Query mutation state and in-memory active context; host auth adapter
 * @allowed-imports app environment/session; contracts; shared transient reset; TanStack Query; same module query keys
 * @forbidden localStorage; Zustand role ownership; permission inference
 * @public-api auth module
 * @side-effects identity-context reads, personal-workspace provisioning, Query cache cleanup and host sign-out
 * @decision The audience login URL selects the context while backend memberships remain authoritative.
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright role and sign-out flows
 * @security-boundary authenticated context mutation and session teardown
 * @data-classification membership context and in-memory identity
 * @trust-model backend response is authoritative; frontend selection is not authorization
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ActiveContext, meContextResponseSchema, type Role } from '@vispo/contracts';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { resetWorkspaceTransientState } from '../../../shared/lib/transient-reset';
import { authKeys } from './auth.queries';
import { ensurePersonalWorkspace, loadMeContext } from './identity-context';

export function contextFromMemberships(
  value: unknown,
  userId: string,
  role: Role,
  selectedOrganizationId?: string | null,
): ActiveContext {
  const response = meContextResponseSchema.parse(value);
  const selectedOrganization = selectedOrganizationId
    ? response.organizations.find((candidate) => candidate.id === selectedOrganizationId)
    : undefined;
  const organization =
    role === 'teacher'
      ? selectedOrganizationId
        ? selectedOrganization &&
          (['owner', 'admin', 'staff'].includes(selectedOrganization.membership_role) ||
            selectedOrganization.is_classroom_educator)
          ? selectedOrganization
          : undefined
        : (response.organizations.find(
            (candidate) =>
              candidate.kind === 'personal' &&
              ['owner', 'admin', 'staff'].includes(candidate.membership_role),
          ) ??
          response.organizations.find((candidate) =>
            ['owner', 'admin', 'staff'].includes(candidate.membership_role),
          ))
      : selectedOrganizationId
        ? selectedOrganization?.membership_role === 'member'
          ? selectedOrganization
          : undefined
        : response.organizations.find((candidate) => candidate.membership_role === 'member');
  if (!organization) {
    if (selectedOrganizationId) {
      throw new Error('你目前沒有權限進入這個工作區。');
    }
    if (role === 'student') {
      // A deep link to a specific Organization the student cannot access must refuse like the
      // teacher path does; silently falling back to the synthetic scope leaves the shell waiting
      // forever for an organizationId match that can never arrive.
      // A student who has not joined a class still owns a valid identity and must reach the empty
      // product shell. This synthetic cache scope grants no organization access: every API request
      // remains authorized from the token and simply returns no membership-scoped rows.
      return {
        id: `${userId}:unassigned:student`,
        organizationId: userId,
        classId: null,
        role,
      };
    }
    throw new Error('目前沒有可用的老師工作區');
  }
  return {
    id: `${userId}:${organization.id}:${role}`,
    organizationId: organization.id,
    classId: null,
    role,
  };
}

export function useSelectActiveContext() {
  const environment = useWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      role,
    }: {
      organizationId?: string | null;
      role: Role;
    }): Promise<ActiveContext> => {
      if (!session) throw new Error('請先登入再選擇工作區');
      if (environment.demoMode) {
        const fixture = environment.testAdapter?.read('iamF12Context') as
          | { context?: unknown; status?: string }
          | undefined;
        if (fixture?.status === 'ready' && fixture.context) {
          return contextFromMemberships(fixture.context, session.userId, role, organizationId);
        }
        if (organizationId && organizationId !== session.userId) {
          throw new Error('你目前沒有權限進入這個工作區。');
        }
        return {
          id: `${session.userId}:${role}`,
          organizationId: session.userId,
          classId: null,
          role,
        };
      }
      const response =
        role === 'teacher' && !organizationId
          ? await ensurePersonalWorkspace(
              environment.apiClient,
              `teacher-workspace:${session.userId}`,
            )
          : await loadMeContext(environment.apiClient);
      return contextFromMemberships(response, session.userId, role, organizationId);
    },
    onSuccess: (activeContext) => {
      resetWorkspaceTransientState();
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== authKeys.all[0],
      });
      if (session) {
        queryClient.setQueryData(authKeys.activeContext(session.userId), activeContext);
      }
    },
  });
}

let signedOutAudience: 'student' | 'teacher' | null = null;

/**
 * Consumed once by the neutral /login route: a signed-out portal audience sends the
 * visitor straight back to their own login page instead of the audience chooser.
 */
export function consumeSignedOutAudience(): 'student' | 'teacher' | null {
  const audience = signedOutAudience;
  signedOutAudience = null;
  return audience;
}

export function useSignOut() {
  const { session, signOut } = useWorkspaceSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Capture the portal-audience stamp before signOut clears the session.
      signedOutAudience = session?.portalAudience ?? null;
      try {
        await signOut();
      } catch (error) {
        // A rejected sign-out never cleared the session, so this capture must not leak into
        // a later, unrelated stampless /login visit.
        signedOutAudience = null;
        throw error;
      }
    },
    onSuccess: () => {
      // Sign-out resets the interface language to its default, unlike a context/role switch:
      // a shared device's locale must not carry over into the next user's pre-hydration window.
      resetWorkspaceTransientState({ resetLanguage: true });
      queryClient.clear();
    },
  });
}
